<template>
    <div class="mail-compose">
        <div class="compose-header" v-if="showHeader">
            <div class="compose-title">
                <h2>{{ composeTitleText }}</h2>
            </div>

            <div class="compose-actions">
                <SupsisButton icon="close" @click="$emit('close')" />
            </div>
        </div>

        <div class="compose-form">
            <div class="form-group" v-if="enabledSelectSender">
                <div class="form-label">{{ $t("mail.From") }}:</div>
                <div class="form-field">
                    <MailSelector
                        :value="selectedFrom"
                        :options="fromOptions"
                        :placeholder="$t('mail.From')"
                        :multiple="false"
                        :showWarningForMultiple="false"
                        :auto-focus="autoFocusTo === 'from'"
                        @input="handleFromChange"
                        @search="handleMailSearch($event, 'from')"
                    />
                </div>
            </div>
            <div class="form-group">
                <div class="form-label">{{ $t("mail.To") }}:</div>
                <div class="form-field">
                    <MailSelector
                        :value="formData.toList"
                        :options="toOptions"
                        :placeholder="$t('mail.recipientEmailAddress')"
                        :multiple="true"
                        :showWarningForMultiple="true"
                        :auto-focus="autoFocusTo === 'to'"
                        @input="handleToChange"
                        @search="handleMailSearch($event, 'to')"
                    />
                </div>
                <div class="cc-bcc-actions">
                    <SupsisButton class="mini-btn" @click="showCc = !showCc" :primary="showCc"> CC</SupsisButton>
                    <SupsisButton class="mini-btn" @click="showBcc = !showBcc" :primary="showBcc"> BCC</SupsisButton>
                </div>
            </div>

            <div class="form-group" v-if="showCc">
                <div class="form-label">CC:</div>
                <div class="form-field">
                    <MailSelector
                        :value="formData.ccList"
                        :options="ccOptions"
                        :placeholder="$t('mail.CCreceivers')"
                        :multiple="true"
                        @input="handleCcChange"
                        @search="handleMailSearch($event, 'cc')"
                    />
                </div>
            </div>

            <div class="form-group" v-if="showBcc">
                <div class="form-label">BCC:</div>
                <div class="form-field">
                    <MailSelector
                        :value="formData.bccList"
                        :options="bccOptions"
                        :placeholder="$t('mail.BCCreceivers')"
                        :multiple="true"
                        @input="handleBccChange"
                        @search="handleMailSearch($event, 'bcc')"
                    />
                </div>
            </div>

            <div class="form-group" v-if="!isReply && !isForward">
                <div class="form-label">{{ $t("message.subject") }}</div>
                <div class="form-field">
                    <input
                        v-model="formData.subject"
                        :placeholder="$t('mail.emailSubject')"
                        class="subject-input custom-input"
                        ref="mailSubject"
                    />
                </div>
            </div>

            <div class="signature-prompt" v-if="askForSignature && mailSignature && mailSignature.html">
                <div class="signature-message">
                    <i class="material-icons">info</i>
                    <span>{{ $t("mail.signatureMessage") }}</span>
                </div>
                <div class="signature-actions">
                    <SupsisButton small @click="askForSignature = false">{{ $t("chatbotv2.no") }}</SupsisButton>
                    <SupsisButton small primary @click="addSignatureAndClosePrompt"
                        >{{ $t("chatbotv2.yes") }}
                    </SupsisButton>
                </div>
            </div>

            <div class="editor-container">
                <MailEditor
                    v-model="formData.body"
                    ref="mailEditor"
                    :auto-focus="autoFocusTo === 'editor'"
                    @image-upload="handleImageUpload"
                    @ai-assistant="onAIAssistant"
                />
                <input type="file" ref="imageFileInput" style="display: none" @change="onImageFileSelected" />
            </div>

            <div class="form-attachments" v-if="formData.attachments.length > 0">
                <div class="attachments-title">
                    {{ $t("mail.emailAttachments", { count: formData.attachments.length }) }}
                </div>
                <div class="attachments-list">
                    <div v-for="(attachment, index) in formData.attachments" :key="index" class="attachment-item">
                        <i class="material-icons">{{ getAttachmentIcon(attachment.name) }}</i>
                        <div class="attachment-info">
                            <div class="attachment-name">{{ attachment.name }}</div>
                            <div class="attachment-size">{{ attachment.size }}</div>
                        </div>
                        <SupsisButton icon="close" @click="removeAttachment(index)" xxsmall />
                    </div>
                </div>
            </div>

            <div class="compose-actions-bar">
                <SupsisButton @click="$emit('close')"> {{ $t("mail.cancel") }}</SupsisButton>
                <SupsisButton primary @click="sendMail" :disabled="!canSend"> {{ $t("message.send") }}</SupsisButton>
            </div>
        </div>

        <MailAIAssistant
            v-if="showAIAssistant"
            :mail-content="formData.body"
            :mail-details="mailDetails"
            :threadMails="threadMails"
            @close="showAIAssistant = false"
            @update-content="updateMailContent"
        />
    </div>
</template>

<script>
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import MailEditor from "./MailEditor.vue";
import MailSelector from "./MailSelector.vue";
import MailAIAssistant from "./MailAIAssistant.vue";
import { mapActions, mapState, mapGetters } from "vuex";
import { generateUUID } from "../../../../../utils/session";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import axios from "axios";
import _ from "lodash";
import { uploadAsset } from "../../../../../api/assetUpload";

export default {
    name: "MailCompose",
    components: {
        SupsisButton,
        MailEditor,
        MailSelector,
        MailAIAssistant,
    },
    props: {
        mailData: {
            type: Object,
            required: true,
        },
        isReply: {
            type: Boolean,
            default: false,
        },
        isForward: {
            type: Boolean,
            default: false,
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
        threadMails: {
            type: Array,
            default: () => [],
        },
        selectedEmailId: {
            type: String,
            default: "",
        },
        enabledSelectSender: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        // Eğer to, cc, bcc string ise dizi haline dönüştürme
        const toList = this.mailData.to
            ? typeof this.mailData.to === "string"
                ? this.mailData.to.split(/[,;\s]+/).filter(Boolean)
                : this.mailData.to
            : [];

        const ccList = this.mailData.cc
            ? typeof this.mailData.cc === "string"
                ? this.mailData.cc.split(/[,;\s]+/).filter(Boolean)
                : this.mailData.cc
            : [];

        const bccList = this.mailData.bcc
            ? typeof this.mailData.bcc === "string"
                ? this.mailData.bcc.split(/[,;\s]+/).filter(Boolean)
                : this.mailData.bcc
            : [];

        let autoFocusTo = "editor";
        if (!this.selectedEmailId) autoFocusTo = "from";
        if (this.selectedEmailId && !toList.length) autoFocusTo = "to";
        if (this.selectedEmailId && toList.length > 0 && !this.mailData.subject) autoFocusTo = "subject";

        return {
            autoFocusTo,
            selectedFrom: null,
            formData: {
                to: this.mailData.to || "",
                cc: this.mailData.cc || "",
                bcc: this.mailData.bcc || "",
                toList: toList,
                ccList: ccList,
                bccList: bccList,
                subject: this.mailData.subject || "",
                body: this.mailData.body || "",
                attachments: this.mailData.attachments ? [...this.mailData.attachments] : [],
            },
            showCc: (this.mailData.cc || "").length > 0 ? true : false,
            showBcc: (this.mailData.bcc || "").length > 0 ? true : false,
            emailOptions: {
                to: [],
                cc: [],
                bcc: [],
            },
            cancels: [], // İnterval ve listener referansları için dizi
            showAIAssistant: false,
            signatureAdded: false, // İmza eklenip eklenmediğini kontrol etmek için
            askForSignature: false, // İmza ekleme sorusunu göstermek için
            mailSignature: null, // İmza bilgilerini saklamak için
        };
    },
    computed: {
        ...mapGetters({
            allowedMailAddresses: "mail/allowedMailAddresses",
            defaultMail: "mail/defaultMail",
        }),
        ...mapState({
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            departments: (state) => {
                const user = state.auth.user;
                return state.subject.subjects.data.reduce((acc, curr) => {
                    const firstLangKey = curr.languageContent[user.lang]
                        ? user.lang
                        : Object.keys(curr.languageContent)[0];
                    const firstLangName = firstLangKey ? _.get(curr, `languageContent.${firstLangKey}.name`) : null;
                    acc[curr._id] = firstLangName || "Deleted";
                    return acc;
                }, {});
            },
            users: (state) =>
                state.user.users.data.reduce((acc, curr) => {
                    acc[curr._id] = curr.name;
                    return acc;
                }, {}),
        }),
        composeTitleText() {
            if (this.isReply) return this.$t("message.replyMessage");
            if (this.isForward) return this.$t("message.forwardRep");
            return this.$t("mail.newMail");
        },
        canSend() {
            return this.formData.toList.length > 0;
        },
        fromOptions() {
            return this.allowedMailAddresses
                .filter((mail) => (mail.isSenderMail ? mail.isSenderVerified : mail.isSender))
                .map((option) => {
                    const labels = {
                        user: this.users[_.get(option, "user._id", option.user)] || "Deleted",
                        department: this.departments[option.department] || "Deleted",
                    };
                    return {
                        type: option.type,
                        label: labels[option.type],
                        value: option.mail,
                    };
                });
        },
        toOptions() {
            return this.emailOptions.to.map((option) => {
                const labels = {
                    user: this.users[_.get(option, "user._id", option.user)] || "Deleted",
                    department: this.departments[option.department] || "Deleted",
                    visitor: _.get(option, "visitor.fullname") || "Deleted",
                };
                return {
                    type: option.type,
                    label: labels[option.type],
                    value: option.mail,
                };
            });
        },
        ccOptions() {
            return this.emailOptions.cc.map((option) => {
                const labels = {
                    user: this.users[_.get(option, "user._id", option.user)] || "Deleted",
                    department: this.departments[option.department] || "Deleted",
                    visitor: _.get(option, "visitor.fullname") || "Deleted",
                };
                return {
                    type: option.type,
                    label: labels[option.type],
                    value: option.mail,
                };
            });
        },
        bccOptions() {
            return this.emailOptions.bcc.map((option) => {
                const labels = {
                    user: this.users[_.get(option, "user._id", option.user)] || "Deleted",
                    department: this.departments[option.department] || "Deleted",
                    visitor: _.get(option, "visitor.fullname") || "Deleted",
                };
                return {
                    type: option.type,
                    label: labels[option.type],
                    value: option.mail,
                };
            });
        },
        selectedEmail() {
            const allowedMailAddresses = this.allowedMailAddresses;
            const [selectedFrom] = this.selectedFrom || [];
            return allowedMailAddresses.find((mail) => mail._id === this.selectedEmailId || mail.mail === selectedFrom);
        },
        mailDetails() {
            return {
                subject: this.formData.subject,
                to: this.formData.to,
                cc: this.formData.cc,
                bcc: this.formData.bcc,
                from: this.selectedEmail?.mail || "",
                threadId: this.mailData.threadId,
                messageId: this.mailData.messageId,
                createdAt: new Date(),
            };
        },
    },
    mounted() {
        this.initSelectedFrom();
        this.checkSignature();
        setTimeout(() => {
            this.$nextTick(() => {
                const mailSubject = this.$refs.mailSubject;
                if (mailSubject && this.autoFocusTo === "subject") mailSubject.focus();
            });
        }, 100);
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    methods: {
        ...mapActions({
            getMailAddresses: "mail/getMailAddresses",
            mailVerification: "mail/mailVerification",
            createVisitorAndMailAddressMulti: "mail/createVisitorAndMailAddressMulti",
            getMailAddressByToList: "mail/getMailAddressByToList",
            getSenderMailAddressesByInput: "mail/getSenderMailAddressesByInput",
            getDepartmentMailAddresses: "mail/getDepartmentMailAddresses",
            getMailSignature: "mail/getMailSignature",
        }),
        initSelectedFrom() {
            if (this.selectedEmailId) {
                const selectedEmail = this.allowedMailAddresses.find(
                    (mail) => mail._id === this.selectedEmailId || mail.mail === this.selectedEmailId
                );
                if (selectedEmail) this.selectedFrom = [this.selectedEmail.mail];
            } else if (this.defaultMail) {
                this.selectedFrom = [this.defaultMail.mail];
                if (this.formData.toList.length > 0 && !this.mailData.subject) {
                    this.autoFocusTo = "subject";
                }
            }
        },
        handleFromChange(value) {
            this.selectedFrom = value;
        },
        sendMail() {
            if (!this.canSend) return;

            this.$emit("loading", true);

            const mailList = [
                ...this.formData.toList,
                ...this.formData.ccList,
                ...this.formData.bccList,
                this.formData.to,
            ];

            // Mail adreslerinin geçerliliğini kontrol et
            const validEmails = mailList.every((email) => {
                const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                return regex.test(email);
            });

            if (!validEmails) {
                this.$message.error(this.$t("mail.emailAddressInvalid"));
                this.$emit("loading", false);
                return;
            }

            // Ziyaretçi ve mail adreslerini oluştur
            this.createVisitorAndMailAddressMulti({
                siteId: this.site._id,
                mailList,
                type: "visitor",
            })
                .then(() => {
                    // Mail adreslerini al
                    this.getMailAddressByToList({ mailList })
                        .then(async (mailAddresses) => {
                            // Ziyaretçileri ve diğer referansları ayır
                            let ref_visitor_ids = _.uniqBy(
                                mailAddresses.filter((item) => item.type === "visitor"),
                                "mail"
                            );
                            const ref_user_ids = mailAddresses
                                .filter((item) => item.type === "user")
                                .map((item) => item.user);
                            const ref_department_ids = mailAddresses
                                .filter((item) => item.type === "department")
                                .map((item) => item.department);

                            // Thread ve message ID oluşturma
                            const generateThreadId = () => {
                                let generateUuid = generateUUID();
                                const uuidSplitList = generateUuid.split("-");
                                generateUuid = uuidSplitList[uuidSplitList.length - 1];
                                return generateUuid;
                            };

                            // Konu belirleme
                            let subject = this.formData.subject;
                            if (this.isReply) {
                                subject = this.mailData.subject;
                            } else if (this.isForward) {
                                subject = this.mailData.subject;
                            }

                            // Thread ID belirleme
                            let threadId;
                            if (this.isReply) {
                                // Yanıt ise aynı thread ID'yi kullan
                                threadId = this.mailData.threadId;
                            } else {
                                // Yeni mail veya forward ise yeni thread ID oluştur
                                threadId = generateThreadId();
                            }

                            // Yanıtlanan mesaj ID'si
                            const replyToMessageId = this.isReply ? this.mailData.messageId : null;

                            // Mail içeriğini forward veya reply formatına göre düzenle
                            let finalBodyHtml = this.processBodyForSending(this.formData.body);

                            // Mesaj içeriğinden HTML ve text içeriği oluşturma
                            const htmlElement = document.createElement("div");
                            htmlElement.innerHTML = finalBodyHtml;
                            const bodyText = htmlElement.textContent || htmlElement.innerText || "";

                            // Eğer <collapsed-mail> etiketi varsa, içeriği olduğu gibi koruyalım
                            if (
                                this.isForward &&
                                !finalBodyHtml.includes("<collapsed-mail>") &&
                                !finalBodyHtml.includes("---------- Yönlendirilen mesaj ---------")
                            ) {
                                finalBodyHtml = `
                            <br/><br/>
                            <p>---------- Yönlendirilen mesaj ---------</p>
                            <p><b>Kimden:</b> ${this.mailData.from || ""}</p>
                            <p><b>Tarih:</b> ${this.$moment(this.mailData.createdAt || new Date()).format(
                                "DD MMMM YYYY, HH:mm"
                            )}</p>
                            <p><b>Konu:</b> ${this.mailData.subject || ""}</p>
                            <p><b>Kime:</b> ${this.mailData.to || ""}</p>
                            ${this.mailData.cc ? `<p><b>Cc:</b> ${this.mailData.cc}</p>` : ""}
                            <hr/>
                            ${finalBodyHtml}
                        `;
                            } else if (
                                this.isReply &&
                                !finalBodyHtml.includes("--------- Orijinal Mesaj ---------")
                            ) {
                                finalBodyHtml = `
                            ${finalBodyHtml}
                            <br/><br/>
                            <p>--------- Orijinal Mesaj ---------</p>
                            <p><b>Kimden:</b> ${this.mailData.from || ""}</p>
                            <p><b>Tarih:</b> ${this.$moment(this.mailData.createdAt || new Date()).format(
                                "DD MMMM YYYY, HH:mm"
                            )}</p>
                            <p><b>Konu:</b> ${this.mailData.subject || ""}</p>
                            <p><b>Kime:</b> ${this.mailData.to || ""}</p>
                            ${this.mailData.cc ? `<p><b>Cc:</b> ${this.mailData.cc}</p>` : ""}
                            <hr/>
                        `;
                            }

                            // Mail gönderme işlemi
                            if (ref_visitor_ids.length > 0) {
                                // Her bir ziyaretçi için mail gönder
                                ref_visitor_ids.forEach((visitor) => {
                                    const mailData = {
                                        sender: this.selectedEmail.mail,
                                        to_list: this.formData.toList,
                                        cc_list: this.formData.ccList,
                                        bcc_list: this.formData.bccList,
                                        ref_user_ids: [...ref_user_ids, this.user._id],
                                        ref_department_ids,
                                        ref_visitor_id: visitor.visitor,
                                        subject: subject,
                                        body_html: finalBodyHtml,
                                        body_text: bodyText,
                                        replyToAddresses: [],
                                        files: this.formData.attachments,
                                        attachments: this.formData.attachments.map((att) => att.id || ""),
                                        fileNames: this.formData.attachments.map((att) => att.name),
                                        thread_id: threadId,
                                        replay_to_message_id: replyToMessageId,
                                        senderName: this.user.name,
                                    };

                                    sendSocketMessage("email send", {
                                        siteId: this.site._id,
                                        userId: this.user._id,
                                        data: mailData,
                                    });
                                });
                            } else {
                                // Ziyaretçi yoksa genel mail gönder
                                const mailData = {
                                    sender: this.selectedEmail.mail,
                                    to_list: this.formData.toList,
                                    cc_list: this.formData.ccList,
                                    bcc_list: this.formData.bccList,
                                    ref_user_ids: [...ref_user_ids, this.user._id],
                                    ref_department_ids,
                                    ref_visitor_id: "",
                                    subject: subject,
                                    body_html: finalBodyHtml,
                                    body_text: bodyText,
                                    replyToAddresses: [],
                                    files: this.formData.attachments,
                                    attachments: this.formData.attachments.map((att) => att.id || ""),
                                    fileNames: this.formData.attachments.map((att) => att.name),
                                    thread_id: threadId,
                                    replay_to_message_id: replyToMessageId,
                                    senderName: this.user.name,
                                };

                                sendSocketMessage("email send", {
                                    siteId: this.site._id,
                                    userId: this.user._id,
                                    data: mailData,
                                });
                            }

                            this.$emit("loading", false);
                            this.$emit("close");
                            this.$emit("sent", true);
                        })
                        .catch((error) => {
                            this.$message.error(this.$t("mail.sendError"));
                            this.$emit("loading", false);
                        });
                })
                .catch((error) => {
                    this.$message.error(this.$t("mail.visitorCreationError"));
                    this.$emit("loading", false);
                });
        },
        handleToChange(values) {
            this.formData.toList = values;
            this.formData.to = values.join(", ");
        },
        handleCcChange(values) {
            this.formData.ccList = values;
            this.formData.cc = values.join(", ");
        },
        handleBccChange(values) {
            this.formData.bccList = values;
            this.formData.bcc = values.join(", ");
        },
        handleMailSearch(query, field) {
            let selectedMembers = [];
            if (field === "to") selectedMembers = this.formData.toList;
            if (field === "cc") selectedMembers = this.formData.ccList;
            if (field === "bcc") selectedMembers = this.formData.bccList;

            this.getMailAddresses({
                userId: this.user._id,
                inputValue: query,
                selectedMailAddress: selectedMembers,
            }).then((emails) => {
                this.emailOptions[field] = emails;
            });
        },
        handleImageUpload() {
            this.$refs.imageFileInput.click();
        },
        onImageFileSelected(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (file.size > 10 * 1024 * 1024) {
                this.$message.error("Dosya boyutu 10 MB'dan büyük olamaz");
                return;
            }

            this.uploadImageFile(file);

            event.target.value = null;
        },
        async uploadImageFile(file) {
            try {
                // Yükleme başladığını bildir
                this.$emit("loading", true);

                // Dosya ismini ve boyutunu oluştur
                const newSize = (file.size / 1024).toFixed(0) + " KB";

                // Dosya uzantısını al
                const fileType = file.name.split(".").pop().toLowerCase();

                // Path oluştur
                const path = `${this.site._id}/email/images/`;

                // Asset yükleme işlemi için API çağrısı
                const { data } = await uploadAsset({
                    path,
                    mime: file.type,
                    ext: fileType || "jpg",
                    name: file.name,
                    size: file.size,
                    type: "mail",
                });

                // Ekleri güncelle
                const fileId = generateUUID();
                this.formData.attachments.push({
                    id: data.asset.id,
                    name: file.name,
                    size: newSize,
                    fileId: fileId,
                    file: file,
                    isImage: true,
                });

                // CDN'e dosya yükle
                await axios({
                    method: data.method,
                    url: data.url,
                    data: file,
                    headers: {
                        "Content-Type": file.type,
                    },
                    onUploadProgress: function (progressEvent) {
                        const percentCompleted = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    },
                });

                this.$emit("loading", false);
                this.$message.success("Dosya başarılı bir şekilde içe aktarıldı");
            } catch (error) {
                this.$emit("loading", false);
                this.$message.error(this.$t("mail.imageUploadError"));
            }
        },
        removeAttachment(index) {
            this.formData.attachments.splice(index, 1);
        },
        getAttachmentIcon(filename) {
            const extension = (filename || "").split(".").pop().toLowerCase();

            switch (extension) {
                case "pdf":
                    return "picture_as_pdf";
                case "doc":
                case "docx":
                    return "description";
                case "xls":
                case "xlsx":
                    return "table_chart";
                case "ppt":
                case "pptx":
                    return "slideshow";
                case "jpg":
                case "jpeg":
                case "png":
                case "gif":
                    return "image";
                case "zip":
                case "rar":
                    return "folder_zip";
                default:
                    return "attach_file";
            }
        },
        onAIAssistant() {
            // AI asistanı göster
            this.showAIAssistant = true;
        },
        updateMailContent(newContent) {
            // AI tarafından oluşturulan içeriği mail içeriğine uygula
            this.formData.body = newContent;

            // Editör referansı varsa içeriği güncelle
            if (this.$refs.mailEditor && this.$refs.mailEditor.editor) {
                this.$refs.mailEditor.editor.commands.setContent(newContent);
            }
        },
        async checkSignature() {
            // İmza daha önce eklenmediyse
            if (!this.signatureAdded) {
                const senderMail = this.selectedEmail?.mail || "";
                if (senderMail) {
                    try {
                        // Gönderici mail adresine göre imza bilgilerini al
                        const signature = await this.getMailSignature({ mailAddress: senderMail });
                        this.mailSignature = signature;

                        // Otomatik ekleme varsa imzayı ekle
                        if (signature && signature.isAutoAdd) {
                            this.addSignatureToBody(signature.html);
                            this.signatureAdded = true;
                        }
                        // Otomatik ekleme yoksa ve imza varsa sor
                        else if (signature && signature.html) {
                            this.askForSignature = true;
                        }
                    } catch (error) {}
                }
            }
        },
        addSignatureToBody(signatureHtml) {
            if (this.signatureAdded) return;
            // Editör yoksa veya boşsa doğrudan ekle
            if (!this.formData.body || this.formData.body === "") {
                this.formData.body = `<br><br>${signatureHtml}`;
            }
            // Varolan içeriğin sonuna ekle
            else {
                this.formData.body = `${this.formData.body}<br><br>${signatureHtml}`;
            }

            // Eğer editör referansı varsa içeriği güncelle
            if (this.$refs.mailEditor && this.$refs.mailEditor.editor) {
                this.$refs.mailEditor.editor.commands.setContent(this.formData.body);
                // cursor en başa geçsin.
                this.$refs.mailEditor.editor.commands.setTextSelection(0);
            }
        },
        processBodyForSending(html) {
            var div = document.createElement("div");
            div.innerHTML = html;

            var collapsedMails = div.querySelectorAll("collapsed-mail");
            for (var i = 0; i < collapsedMails.length; i++) {
                var el = collapsedMails[i];
                var content = el.getAttribute("content");
                if (content) {
                    el.innerHTML = content;
                    el.removeAttribute("content");
                }
            }

            return div.innerHTML;
        },
        addSignatureAndClosePrompt() {
            if (this.signatureAdded) return;
            this.addSignatureToBody(this.mailSignature.html);
            this.signatureAdded = true;
            this.askForSignature = false;
        },
    },
    watch: {
        // Seçili mail değiştiğinde imza kontrolü yap
        "selectedEmail.mail": {
            handler: async function (newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.signatureAdded = false;
                    await this.checkSignature();
                }
            },
        },
        allowedMailAddresses: {
            handler: function (newVal, oldVal) {
                this.initSelectedFrom();
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.mail-compose {
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: #fff;

    .compose-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid #eaeaea;
        background-color: #f9f9f9;

        .compose-title {
            h2 {
                margin: 0;
                font-size: 20px;
                font-weight: 600;
                color: #22272d;
            }
        }
    }

    .compose-form {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 20px;
        overflow: auto;

        /* Özel scrollbar stilleri */
        &::-webkit-scrollbar {
            width: 4px;
            /* İnce scrollbar genişliği */
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb {
            background: #22272d;
            border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: #1a1f23;
        }

        /* Firefox için scrollbar stilleri */
        scrollbar-width: thin;
        scrollbar-color: #22272d #f1f1f1;

        .form-group {
            display: flex;
            margin-bottom: 16px;
            position: relative;

            .form-label {
                width: 70px;
                min-width: 70px;
                padding-top: 8px;
                font-weight: 500;
                color: #444;
            }

            .form-field {
                flex: 1;

                :deep(.ant-input),
                :deep(.ant-select-selector),
                :deep(.ant-select-selection) {
                    border-radius: 4px;
                    min-height: 38px;

                    &:hover,
                    &:focus {
                        border-color: #22272d;
                    }
                }

                .custom-input {
                    width: 100%;
                    height: 38px;
                    min-height: 38px;
                    border: 1px solid #d9d9d9;
                    border-radius: 4px;
                    padding: 0 11px;
                    font-size: 14px;

                    &:hover {
                        border-color: #22272d;
                    }

                    &:focus {
                        border-color: #22272d;
                        outline: none;
                        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.2);
                    }

                    &::placeholder {
                        color: #bfbfbf;
                    }
                }

                :deep(.ant-select-selection__rendered) {
                    min-height: 38px;
                    line-height: 38px;
                }

                :deep(.ant-select-selection--multiple) {
                    min-height: 38px;
                }

                :deep(.ant-select-selection__choice) {
                    margin-top: 6px;
                }
            }

            &.readonly-subject {
                .readonly-field {
                    padding: 8px 12px;
                    border-radius: 4px;
                    min-height: 38px;
                    background-color: #f5f5f5;
                    border: 1px solid #e0e0e0;
                    line-height: 22px;
                    color: #555;
                    font-weight: 500;
                }
            }

            .cc-bcc-actions {
                display: flex;
                align-items: center;
                gap: 5px;
                margin-left: 10px;

                .mini-btn {
                    min-width: 32px;
                    height: 22px;
                    padding: 0 6px;
                    font-size: 11px;
                    line-height: 1;
                    border-radius: 3px;
                    font-weight: normal;
                }
            }
        }

        .editor-container {
            border-radius: 4px;
            overflow: hidden;
            margin-bottom: 16px;
            position: relative;
        }

        .form-attachments {
            margin: 20px 0;
            border-top: 1px solid #eaeaea;
            padding-top: 15px;

            .attachments-title {
                font-weight: 500;
                margin-bottom: 10px;
                color: #444;
            }

            .attachments-list {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;

                .attachment-item {
                    display: flex;
                    align-items: center;
                    padding: 8px 12px;
                    background-color: #f5f5f5;
                    border-radius: 4px;
                    border: 1px solid #eaeaea;

                    i {
                        font-size: 20px;
                        margin-right: 8px;
                        color: #666;
                    }

                    .attachment-info {
                        flex: 1;
                        margin-right: 8px;

                        .attachment-name {
                            font-size: 13px;
                            color: #444;
                        }

                        .attachment-size {
                            font-size: 12px;
                            color: #888;
                        }
                    }
                }
            }
        }

        .compose-actions-bar {
            width: 100%;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            border-top: 1px solid #eaeaea;
            padding-top: 20px;
            margin-top: 10px;
            gap: 10px;
        }
    }
}

.menu-item-with-check {
    display: flex;
    align-items: center;

    .check-icon {
        font-size: 16px;
        margin-right: 5px;
    }
}

.menu-item-with-icon {
    display: flex;
    align-items: center;

    i {
        font-size: 18px;
        margin-right: 8px;
    }
}

// İmza sorgusu için stiller
.signature-prompt {
    margin-bottom: 16px;
    padding: 12px 16px;
    background-color: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .signature-message {
        display: flex;
        align-items: center;
        gap: 8px;

        i {
            color: #2196f3;
            font-size: 20px;
        }

        span {
            font-size: 14px;
            color: #444;
        }
    }

    .signature-actions {
        display: flex;
        gap: 8px;
    }
}

// AI Asistanı modalı için stil
:deep(.ai-assistant-modal) {
    .ant-modal-body {
        min-height: 500px;
        max-height: 80vh;
    }
}
</style>
